/**
 * Navigation State Management Store
 * Uses Zustand with persistence for cross-session state retention
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import {
  NavigationConfig,
  NavigationContext,
  NavigationState,
  NavigationTreeNode,
  NavigationSystemConfig,
  DEFAULT_NAVIGATION_CONFIG,
} from '../types/navigation.types';
import { navigationEvaluator } from '../lib/navigation/NavigationEvaluator';

interface NavigationStore extends NavigationState {
  // Configuration
  config: NavigationSystemConfig;
  navigationItems: NavigationConfig[];
  
  // Context
  currentContext: NavigationContext | null;
  
  // Computed tree (memoized)
  evaluatedTree: NavigationTreeNode[];
  treeLastUpdated: number;
  
  // Actions - State Management
  setCollapsedSections: (sections: string[]) => void;
  toggleSection: (sectionId: string) => void;
  collapseAll: () => void;
  expandAll: () => void;
  
  // Actions - Pinned Items
  pinItem: (itemId: string) => void;
  unpinItem: (itemId: string) => void;
  togglePin: (itemId: string) => void;
  
  // Actions - Recent Access
  addToRecent: (itemId: string) => void;
  clearRecent: () => void;
  
  // Actions - Context Management
  setContext: (context: NavigationContext) => void;
  updateContext: (partial: Partial<NavigationContext>) => void;
  
  // Actions - Navigation Items
  setNavigationItems: (items: NavigationConfig[]) => void;
  
  // Actions - Sidebar
  toggleSidebar: () => void;
  setSidebarCollapsed: (collapsed: boolean) => void;
  setMobileSidebarOpen: (open: boolean) => void;
  toggleCompactMode: () => void;
  
  // Actions - Search
  addToSearchHistory: (query: string) => void;
  clearSearchHistory: () => void;
  
  // Actions - Tree Evaluation
  rebuildTree: () => void;
  getTreeNode: (itemId: string) => NavigationTreeNode | null;
  
  // Actions - Configuration
  updateConfig: (partial: Partial<NavigationSystemConfig>) => void;
  
  // Utilities
  reset: () => void;
}

const initialState: NavigationState = {
  collapsedSections: [],
  pinnedItems: [],
  recentlyAccessed: [],
  customOrder: undefined,
  lastActiveContext: {
    organizationId: '',
    nodeId: undefined,
    moduleContext: '',
    timestamp: Date.now(),
  },
  sidebarCollapsed: false,
  mobileSidebarOpen: false,
  compactMode: false,
  searchHistory: [],
};

export const useNavigationStore = create<NavigationStore>()(
  persist(
    (set, get) => ({
      // Initial state
      ...initialState,
      config: DEFAULT_NAVIGATION_CONFIG,
      navigationItems: [],
      currentContext: null,
      evaluatedTree: [],
      treeLastUpdated: 0,

      // State Management Actions
      setCollapsedSections: (sections) =>
        set({ collapsedSections: sections }),

      toggleSection: (sectionId) =>
        set((state) => {
          const isCollapsed = state.collapsedSections.includes(sectionId);
          const newCollapsedSections = isCollapsed
            ? state.collapsedSections.filter((id) => id !== sectionId)
            : [...state.collapsedSections, sectionId];
          
          // Rebuild tree with new collapsed sections
          const evaluatedTree = buildNavigationTree(
            state.navigationItems,
            state.currentContext!,
            newCollapsedSections,
            0
          );
          
          return {
            collapsedSections: newCollapsedSections,
            evaluatedTree,
            treeLastUpdated: Date.now(),
          };
        }),

      collapseAll: () =>
        set((state) => ({
          collapsedSections: state.navigationItems.map((item) => item.id),
        })),

      expandAll: () => set({ collapsedSections: [] }),

      // Pinned Items Actions
      pinItem: (itemId) =>
        set((state) => {
          if (state.pinnedItems.includes(itemId)) return state;
          return {
            pinnedItems: [...state.pinnedItems, itemId],
          };
        }),

      unpinItem: (itemId) =>
        set((state) => ({
          pinnedItems: state.pinnedItems.filter((id) => id !== itemId),
        })),

      togglePin: (itemId) =>
        set((state) => {
          const isPinned = state.pinnedItems.includes(itemId);
          return {
            pinnedItems: isPinned
              ? state.pinnedItems.filter((id) => id !== itemId)
              : [...state.pinnedItems, itemId],
          };
        }),

      // Recent Access Actions
      addToRecent: (itemId) =>
        set((state) => {
          // Remove if already exists
          const filtered = state.recentlyAccessed.filter((id) => id !== itemId);
          // Add to front, keep max 5
          return {
            recentlyAccessed: [itemId, ...filtered].slice(0, 5),
          };
        }),

      clearRecent: () => set({ recentlyAccessed: [] }),

      // Context Management Actions
      setContext: (context) =>
        set(() => {
          // Update last active context
          const lastActiveContext = {
            organizationId: context.organizationId,
            nodeId: context.selectedNodeId,
            moduleContext: context.organizationModules[0] || '',
            timestamp: Date.now(),
          };

          return {
            currentContext: context,
            lastActiveContext,
          };
        }),

      updateContext: (partial) =>
        set((state) => {
          if (!state.currentContext) return state;
          
          const updatedContext = {
            ...state.currentContext,
            ...partial,
          };

          return {
            currentContext: updatedContext,
            lastActiveContext: {
              organizationId: updatedContext.organizationId,
              nodeId: updatedContext.selectedNodeId,
              moduleContext: updatedContext.organizationModules[0] || '',
              timestamp: Date.now(),
            },
          };
        }),

      // Navigation Items Actions
      setNavigationItems: (items) =>
        set({
          navigationItems: items,
          treeLastUpdated: 0, // Force rebuild
        }),

      // Sidebar Actions
      toggleSidebar: () =>
        set((state) => ({
          sidebarCollapsed: !state.sidebarCollapsed,
        })),

      setSidebarCollapsed: (collapsed) =>
        set({ sidebarCollapsed: collapsed }),

      setMobileSidebarOpen: (open) =>
        set({ mobileSidebarOpen: open }),

      toggleCompactMode: () =>
        set((state) => ({
          compactMode: !state.compactMode,
        })),

      // Search Actions
      addToSearchHistory: (query) =>
        set((state) => {
          // Remove if already exists
          const filtered = state.searchHistory.filter((q) => q !== query);
          // Add to front, keep max 10
          return {
            searchHistory: [query, ...filtered].slice(0, 10),
          };
        }),

      clearSearchHistory: () => set({ searchHistory: [] }),

      // Tree Evaluation Actions
      rebuildTree: () => {
        const state = get();
        const { navigationItems, currentContext, config } = state;

        if (!currentContext || !config.enableMemoization) {
          return;
        }

        // Check cache timeout
        const now = Date.now();
        const cacheAge = now - state.treeLastUpdated;
        if (cacheAge < config.cacheTimeout && state.evaluatedTree.length > 0) {
          return; // Use cached tree
        }

        // Build new tree
        const evaluatedTree = buildNavigationTree(
          navigationItems,
          currentContext,
          state.collapsedSections,
          0
        );

        set({
          evaluatedTree,
          treeLastUpdated: now,
        });
      },

      getTreeNode: (itemId) => {
        const state = get();
        return findNodeById(state.evaluatedTree, itemId);
      },

      // Configuration Actions
      updateConfig: (partial) =>
        set((state) => ({
          config: { ...state.config, ...partial },
        })),

      // Utilities
      reset: () =>
        set({
          ...initialState,
          config: DEFAULT_NAVIGATION_CONFIG,
          navigationItems: [],
          currentContext: null,
          evaluatedTree: [],
          treeLastUpdated: 0,
        }),
    }),
    {
      name: 'nabhos-navigation-state',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        // Only persist these fields
        collapsedSections: state.collapsedSections,
        pinnedItems: state.pinnedItems,
        recentlyAccessed: state.recentlyAccessed,
        customOrder: state.customOrder,
        lastActiveContext: state.lastActiveContext,
        sidebarCollapsed: state.sidebarCollapsed,
        compactMode: state.compactMode,
        searchHistory: state.searchHistory,
        // mobileSidebarOpen is intentionally NOT persisted — always starts closed
      }),
    }
  )
);

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Build evaluated navigation tree with filtering
 */
function buildNavigationTree(
  items: NavigationConfig[],
  context: NavigationContext,
  collapsedSections: string[],
  depth: number
): NavigationTreeNode[] {
  const tree: NavigationTreeNode[] = [];

  for (const item of items) {
    // Evaluate visibility
    const evaluation = navigationEvaluator.evaluate(item, context);

    if (!evaluation.visible) {
      continue; // Skip hidden items
    }

    // Recursively build children
    const children = item.children
      ? buildNavigationTree(item.children, context, collapsedSections, depth + 1)
      : undefined;

    const hasVisibleChildren = children && children.length > 0;

    // Create tree node
    const node: NavigationTreeNode = {
      ...item,
      visible: evaluation.visible,
      enabled: evaluation.enabled,
      depth,
      hasVisibleChildren: hasVisibleChildren || false,
      isActive: false, // Will be set by router
      isExpanded: !collapsedSections.includes(item.id),
      children,
      badgeValue: evaluation.badge?.value,
    };

    tree.push(node);
  }

  return tree;
}

/**
 * Find node by ID in tree
 */
function findNodeById(
  tree: NavigationTreeNode[],
  itemId: string
): NavigationTreeNode | null {
  for (const node of tree) {
    if (node.id === itemId) {
      return node;
    }

    if (node.children) {
      const found = findNodeById(node.children, itemId);
      if (found) return found;
    }
  }

  return null;
}

/**
 * Hook to get current navigation context
 */
export function useNavigationContext() {
  return useNavigationStore((state) => state.currentContext);
}

/**
 * Hook to get evaluated navigation tree
 */
export function useNavigationTree() {
  const store = useNavigationStore();
  
  // Rebuild tree if needed
  if (store.currentContext && store.evaluatedTree.length === 0) {
    store.rebuildTree();
  }

  return store.evaluatedTree;
}

/**
 * Hook to check if item is pinned
 */
export function useIsItemPinned(itemId: string) {
  return useNavigationStore((state) => state.pinnedItems.includes(itemId));
}

/**
 * Hook to check if section is collapsed
 */
export function useIsSectionCollapsed(sectionId: string) {
  return useNavigationStore((state) =>
    state.collapsedSections.includes(sectionId)
  );
}

// Made with Bob
